<template>
  <div>
    <Head title="Konfigurasi Aplikasi" />
    <h1 class="mb-8 font-bold text-3xl">
      <Link
        class="text-indigo-400 hover:text-indigo-600"
        :href="route('admin')">
        Admin
      </Link>
      <span class="text-indigo-400 font-medium">/</span>
      {{ title }}
    </h1>

    <div v-if="Object.keys(groupedConfigs).length === 0" class="text-gray-500">
      Tidak ada konfigurasi tersedia.
    </div>

    <div
      v-for="(groupConfigs, groupName) in groupedConfigs"
      :key="groupName"
      class="mb-8 bg-white rounded shadow p-6">
      <div class="flex justify-between items-center mb-4">
        <h2 class="text-xl font-semibold">
          {{ getGroupLabel(groupName) }}
        </h2>
        <button
          v-if="!editableGroups[groupName]"
          @click="enableGroupEdit(groupName)"
          class="btn-indigo text-sm px-4 py-2">
          Ubah
        </button>
        <button
          v-else
          @click="disableGroupEdit(groupName)"
          class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
          Batal
        </button>
      </div>

      <div class="overflow-x-auto">
        <table class="w-full">
          <thead>
            <tr class="text-left border-b">
              <th class="pb-2 font-semibold">
                Konfigurasi
              </th>
              <th class="pb-2 font-semibold">
                Nilai
              </th>
              <th class="pb-2 font-semibold">
                Keterangan
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="config in groupConfigs"
              :key="config.id"
              :class="[
                'border-b',
                isRowChanged(config)
                  ? 'bg-yellow-50 border-l-4 border-yellow-400'
                  : '',
              ]"
              @dblclick="resetRowValue(config)"
              class="cursor-pointer hover:bg-gray-50">
              <td class="py-3">
                <div class="flex items-center">
                  <span class="font-medium">{{ config.label }}</span>
                  <span
                    v-if="isRowChanged(config)"
                    class="ml-2 px-2 py-1 text-xs bg-yellow-200 text-yellow-800 rounded">
                    Diubah
                  </span>
                </div>
              </td>
              <td class="py-3">
                <div v-if="editableGroups[groupName]" class="flex items-center">
                  <TextInput
                    v-if="config.type === 'int'"
                    v-model="formValues[config.id].value"
                    type="number"
                    class="w-full max-w-xs" />
                  <TextInput
                    v-else-if="config.type === 'datetime'"
                    v-model="formValues[config.id].value"
                    v-mask="'##/##/#### ##:##:##'"
                    type="text"
                    input-class="text-center"
                    placeholder="dd/mm/yyyy HH:mm:ss"
                    class="w-full max-w-xs" />
                  <ToggleButton
                    v-else-if="config.type === 'bool'"
                    v-model="formValues[config.id].value" />
                  <select
                    v-else-if="config.type === 'string'"
                    v-model="formValues[config.id].value"
                    class="form-select w-full max-w-xs">
                    <option value="GANJIL">
                      GANJIL
                    </option>
                    <option value="GENAP">
                      GENAP
                    </option>
                  </select>
                </div>
                <div
                  v-else
                  class="font-mono text-sm"
                  :class="
                    isRowChanged(config)
                      ? 'font-bold text-gray-800'
                      : 'text-gray-600'
                  ">
                  {{ displayValue(config) }}
                </div>
              </td>
              <td class="py-3 text-gray-600 text-sm">
                {{ config.description || "-" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div
      v-if="changedCount > 0"
      class="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-50">
      <div class="max-w-7xl mx-auto flex justify-between items-center">
        <div class="text-gray-700">
          <span class="font-semibold">{{ changedCount }}</span> konfigurasi
          berubah
        </div>
        <div class="flex gap-4">
          <LoadingButton
            type="button"
            :loading="form.processing"
            class="btn-gray"
            @click="resetAllChanges">
            Reset
          </LoadingButton>
          <LoadingButton
            type="button"
            :loading="form.processing"
            class="btn-indigo"
            @click="submitChanges">
            Simpan Perubahan ({{ changedCount }})
          </LoadingButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue2'
import { useForm } from '@inertiajs/vue2'
import { format, parse, parseISO } from 'date-fns'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import ToggleButton from '@/Shared/ToggleButton'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  layout: Layout,
  components: {
    Head,
    TextInput,
    Link,
    ToggleButton,
    LoadingButton,
  },
  props: {
    configs: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      title: 'Konfigurasi Aplikasi',
      form: useForm({ configs: [] }),
      formValues: this.setupFormValues(),
      originalValues: this.setupOriginalValues(),
      editableGroups: {},
      groupLabels: {
        general: 'Umum',
        krs_period: 'Periode KRS',
        eos_period: 'Periode EOS',
      },
    }
  },
  computed: {
    groupedConfigs () {
      return this.configs
    },
    changedConfigs () {
      const changed = []
      Object.values(this.groupedConfigs).forEach((groupConfigs) => {
        groupConfigs.forEach((config) => {
          if (this.isRowChanged(config)) {
            changed.push(config.id)
          }
        })
      })
      return changed
    },
    changedCount () {
      return this.changedConfigs.length
    },
  },
  methods: {
    setupFormValues () {
      const values = {}
      Object.values(this.configs).forEach((groupConfigs) => {
        groupConfigs.forEach((config) => {
          values[config.id] = {
            id: config.id,
            value: this.convertValueForForm(config.value, config.type),
          }
        })
      })
      return values
    },
    setupOriginalValues () {
      const original = {}
      Object.values(this.configs).forEach((groupConfigs) => {
        groupConfigs.forEach((config) => {
          original[config.id] = this.convertValueForForm(
            config.value,
            config.type,
          )
        })
      })
      return original
    },
    convertValueForForm (value, type) {
      if (type === 'bool') {
        return value === true || value === '1' || value === 1
      }
      if (type === 'datetime' && value) {
        return format(parseISO(value), 'dd/MM/yyyy HH:mm:ss')
      }
      return value
    },
    displayValue (config) {
      if (config.type === 'bool') {
        return config.value ? 'ON' : 'OFF'
      }
      if (config.type === 'datetime' && config.value) {
        return format(parseISO(config.value), 'dd/MM/yyyy HH:mm:ss')
      }
      return config.value
    },
    isRowChanged (config) {
      const currentValue = this.formValues[config.id]?.value
      const originalValue = this.originalValues[config.id]

      if (config.type === 'bool') {
        const currentBool =
          currentValue === true || currentValue === '1' || currentValue === 1
        const originalBool =
          originalValue === true ||
          originalValue === '1' ||
          originalValue === 1
        return currentBool !== originalBool
      }

      return String(currentValue) !== String(originalValue)
    },
    enableGroupEdit (groupName) {
      this.$set(this.editableGroups, groupName, true)
    },
    disableGroupEdit (groupName) {
      const groupConfigs = this.groupedConfigs[groupName]
      if (groupConfigs) {
        groupConfigs.forEach((config) => {
          this.formValues[config.id].value = this.originalValues[config.id]
        })
      }
      this.$set(this.editableGroups, groupName, false)
    },
    resetRowValue (config) {
      if (this.isRowChanged(config)) {
        this.formValues[config.id].value = this.originalValues[config.id]
      }
    },
    resetAllChanges () {
      if (confirm('Batalkan semua perubahan?')) {
        Object.keys(this.formValues).forEach((id) => {
          this.formValues[id].value = this.originalValues[id]
        })
      }
    },
    findConfigById (id) {
      let found = null
      Object.values(this.groupedConfigs).forEach((groupConfigs) => {
        const config = groupConfigs.find((c) => c.id === id)
        if (config) {
          found = config
        }
      })
      return found
    },
    submitChanges () {
      const changedConfigsData = this.changedConfigs.map((id) => {
        const config = this.findConfigById(id)
        return {
          id,
          value: this.formatValueForSubmit(
            this.formValues[id].value,
            config.type,
          ),
        }
      })

      this.form.configs = changedConfigsData

      this.form.put('/admin/configurations', {
        onSuccess: () => {
          this.editableGroups = {}
          this.originalValues = { ...this.formValues }
          this.form.configs = []
        },
        preserveState: false,
      })
    },
    formatValueForSubmit (value, type) {
      if (type === 'bool') {
        return value ? '1' : '0'
      }
      if (type === 'int') {
        return String(value)
      }
      if (type === 'datetime' && value) {
        const parsedDate = parse(value, 'dd/MM/yyyy HH:mm:ss', new Date())
        return format(parsedDate, 'yyyy-MM-dd HH:mm:ss')
      }
      return value
    },
    getGroupLabel (groupName) {
      return this.groupLabels[groupName] || groupName
    },
  },
}
</script>
